import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { api } from '../../services/apiService';
import { isApiFailure } from '../../sagas/apiFailure';
import { isNativeCapacitorRuntime } from '../../services/nativeRuntime';
import { practiceEmailOptInEnabled } from '../daily-limit/daily-limit';
import { REMINDER, isFirstCompletedQuiz, isNewAccount, reminderState } from './first-quiz-commitment';

// How long the results page holds the slot under the score for this check
// before it goes ahead with the ordinary next step. Only new accounts wait.
export const FIRST_QUIZ_WAIT_MS = 4000;

const sameId = (a, b) => typeof a === 'string' && typeof b === 'string' && a.toLowerCase() === b.toLowerCase();

const hasEmail = (user) => typeof user?.email === 'string' && user.email.trim() !== '';

const OFF = Object.freeze({ status: 'off', reminder: REMINDER.HIDDEN });
const LOADING = Object.freeze({ status: 'loading', reminder: REMINDER.HIDDEN });

/** The quiz-list read: 'ready' with the answer, or 'failed' (the ordinary next step shows). */
const readFirstQuiz = async (userId, examId) => {
  try {
    const quizzes = await api.getStudentQuizHistory(userId);
    if (Array.isArray(quizzes)) return { examId, status: 'ready', first: isFirstCompletedQuiz(quizzes, examId) };
  } catch {
    // Falls through to failed.
  }
  return { examId, status: 'failed', first: false };
};

/** The preference read for "Remind me"; anything unusable hides the button. */
const readReminder = async (userId) => {
  try {
    const res = await api.getPreference(userId);
    return res && !isApiFailure(res) ? reminderState(res) : REMINDER.HIDDEN;
  } catch {
    return REMINDER.HIDDEN;
  }
};

/**
 * Runs `read` while `run` holds, stores its answer, and drops an answer that
 * arrives after the inputs changed. `idle` while not running, `loading` while it reads.
 */
const useRead = (run, read, idle, loading, deps) => {
  const [value, setValue] = useState(idle);
  useEffect(() => {
    if (!run) {
      setValue(idle);
      return undefined;
    }
    let cancelled = false;
    setValue(loading);
    read().then((answer) => {
      if (!cancelled) setValue(answer);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, ...deps]);
  return value;
};

/** The block's state from the reads so far (see useFirstQuiz). */
export const firstQuizDecision = ({ active, list, wantsReminder, preference, waitedOut }) => {
  if (!active || list.status === 'failed' || (list.status === 'ready' && !list.first)) return OFF;
  const preferenceSettled = !wantsReminder || preference.status === 'ready';
  if (list.status === 'ready' && (preferenceSettled || waitedOut)) {
    // A slow preference read only costs the "Remind me" button.
    return { status: 'first', reminder: preference.status === 'ready' ? preference.reminder : REMINDER.HIDDEN };
  }
  return waitedOut ? OFF : LOADING;
};

/**
 * Is this the student's first completed quiz? See first-quiz-commitment.js for
 * the rule. `enabled` is the page's own gate (a student's own quiz, just
 * submitted, on the web, today's allowance read).
 *
 * Reads, all only while enabled:
 * - /user/me from the store, never fetched here: the quiz page loads it while
 *   the student answers. Without it (a reload, a failed lookup) the answer is
 *   "no", at once, and nothing else is read;
 * - for accounts at most 5 days old only: the student's quiz list (a few rows);
 * - and, only while the practice email opt-in is switched on and the account has
 *   an email address: the preference row, for the "Remind me" button.
 *
 * The answer is decided once per quiz and then kept, so the block never pops in
 * after the ordinary next step has been shown, and the reminder state with it.
 *
 * @returns {{ status: 'off'|'loading'|'first', reminder: 'on'|'off'|'hidden' }}
 */
export const useFirstQuiz = ({ enabled, userId, examId }) => {
  const user = useSelector((state) => state.userDetailsFromToken?.userDetails) ?? null;
  // A store holding someone else's /user/me (an account switch without a reload)
  // says nothing about this student.
  const newAccount = Boolean(user) && sameId(String(user.id ?? ''), userId ?? '') && isNewAccount(user.createdAt);
  const active = Boolean(enabled && newAccount && userId && examId);
  const wantsReminder = active && practiceEmailOptInEnabled() && hasEmail(user);

  // Keyed by quiz, so a list read for the previous result never answers this one.
  const list = useRead(active, () => readFirstQuiz(userId, examId),
    { examId: null, status: 'idle', first: false }, { examId, status: 'loading', first: false }, [userId, examId]);
  const preference = useRead(wantsReminder, () => readReminder(userId).then((reminder) => ({ status: 'ready', reminder })),
    { status: 'idle', reminder: REMINDER.HIDDEN }, { status: 'loading', reminder: REMINDER.HIDDEN }, [userId]);

  // The wait runs per quiz, so the previous result's timeout never cuts this one short.
  const [waitedFor, setWaitedFor] = useState(null);
  useEffect(() => {
    if (!active) return undefined;
    const timer = setTimeout(() => setWaitedFor(examId), FIRST_QUIZ_WAIT_MS);
    return () => clearTimeout(timer);
  }, [active, examId]);

  const raw = firstQuizDecision({
    active,
    list: list.examId === examId ? list : { status: 'loading', first: false },
    wantsReminder,
    preference,
    waitedOut: waitedFor === examId,
  });

  // Decided once per quiz, and only once the page has asked (enabled).
  const decided = useRef({ examId: null, value: null });
  if (decided.current.examId !== examId) decided.current = { examId, value: null };
  if (enabled && raw.status !== 'loading' && decided.current.value === null) decided.current.value = raw;

  if (!enabled) return OFF;
  return decided.current.value ?? raw;
};

/**
 * The results page's wiring for the block: whether it shows, whether the slot
 * under the score is held while the check runs, Dismiss (and where focus goes
 * after it), and the daily_limit_shown latch the block and the daily-limit card
 * share, so that event is logged once per result either way.
 *
 * The check runs only once today's allowance has been read (the block is built
 * from it; "ready" also means the quiz has exam types, which the count needs),
 * for a plan with a daily allowance, on the web (the iOS app has no reminder
 * email or calendar file to offer). A failed or skipped allowance read never
 * holds the slot.
 *
 * @param studentJustFinished the student's own quiz, just submitted.
 * @param dailyLimit useResultDailyLimit's result.
 */
export const useFirstQuizSlot = ({ studentJustFinished, userId, examId, dailyLimit }) => {
  const allowanceRead = dailyLimit?.status === 'ready' && dailyLimit.info?.limit !== 0;
  const firstQuiz = useFirstQuiz({
    enabled: Boolean(studentJustFinished) && allowanceRead && !isNativeCapacitorRuntime(),
    userId,
    examId,
  });
  const [dismissed, setDismissed] = useState(false);
  const focusAfterDismiss = useRef(false);
  const dailyLimitShownRef = useRef(false);

  useEffect(() => {
    setDismissed(false);
    dailyLimitShownRef.current = false;
  }, [examId]);

  // After Dismiss, focus moves to the ordinary next step that takes the block's
  // place, so keyboard and screen-reader users are not dropped at <body>.
  useEffect(() => {
    if (!dismissed || !focusAfterDismiss.current) return;
    focusAfterDismiss.current = false;
    const next = document.querySelector('[data-testid="daily-limit-card"], [data-testid="next-quiz-top"]');
    if (next) {
      next.setAttribute('tabindex', '-1');
      next.focus({ preventScroll: true });
    }
  }, [dismissed]);

  const show = firstQuiz.status === 'first' && !dismissed;
  return {
    show,
    // While the check runs (new accounts only, a few seconds at most) the slot
    // stays empty, as it does while the allowance loads.
    held: show || firstQuiz.status === 'loading',
    reminder: firstQuiz.reminder,
    dailyLimitShownRef,
    dismiss: () => {
      focusAfterDismiss.current = true;
      setDismissed(true);
    },
  };
};
